import { Formik } from "formik";
import * as React from "react";
import * as yup from "yup";
import styled from "styled-components";

import Grid from "src/library/components/Hedron";
import { Button } from "src/library/components/Button";
import {
  Form,
  IFormField
} from "src/library/components/Forms";
import { ButtonLink } from "src/library/components/Link";
import CustomerInfoSidebar from "src/core/customers/components/CustomerInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { intlPhoneRegex } from "src/utils/validation/regex";
import { useCustomerApi } from "src/core/customers/api/customers.api";
import { PageProps } from "src/pages/Router/routes";

const createCustomerForm: () => IFormField[] = () => [
  {
    type: "input",
    name: "accountNumber",
    label: "Account Number",
    placeholder: "Account Number",
    validation: yup
      .string()
      .min(1)
      .required("Account number is required")
  },
  {
    type: "input",
    name: "name",
    label: "Name",
    placeholder: "Name",
    validation: yup
      .string()
      .min(1)
      .required("Name is required")
  },
  {
    type: "fieldset",
    name: "address",
    label: "Address",
    fields: [
      {
        type: "input",
        name: "addressLine1",
        label: "Address line 1",
        placeholder: "Address line 1",
        validation: yup.string().notRequired()
      },
      {
        type: "input",
        name: "addressLine2",
        label: "Address line 2",
        placeholder: "Address line 2",
        validation: yup.string().notRequired()
      },
      {
        type: "input",
        name: "addressLine3",
        label: "Address line 3",
        placeholder: "Address line 3",
        validation: yup.string().notRequired()
      },
      {
        type: "input",
        name: "city",
        label: "City",
        placeholder: "City",
        validation: yup.string().notRequired()
      },
      {
        type: "input",
        name: "postcode",
        label: "Postcode",
        placeholder: "Postcode",
        validation: yup.string().notRequired()
      }
    ]
  },
  {
    type: "input",
    name: "contactName",
    label: "Contact Name",
    placeholder: "Contact Name",
    validation: yup.string().notRequired()
  },
  {
    type: "input",
    name: "contactNumber",
    label: "Contact Number",
    placeholder: "Contact Number",
    validation: yup
      .string()
      .matches(intlPhoneRegex, "Please enter a valid telephone number")
      .notRequired()
  },
  {
    type: "input",
    name: "contactEmail",
    label: "Contact Email",
    placeholder: "Contact Email",
    validation: yup
      .string()
      .email()
      .notRequired()
  }
];

/**
 * Main "CreateCustomer" View
 *
 * @returns
 */
const CreateCustomerPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { terminology } = props;
  const [ createCustomerResponse, createCustomer ] = useCustomerApi("CREATE_CUSTOMER");
  const formRef = React.useRef<Formik>(null);

  React.useEffect(() => {
    // On Success reset the form
    if (createCustomerResponse.data) {
      // redirect to customers edit page
      props.history.push(`/admin/customers/${createCustomerResponse.data.id}/drops`);
    }
  }, [ createCustomerResponse ]);

  const submitForm = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <FormWrapper>
          <Form
            ref={formRef}
            error={createCustomerResponse.error ? createCustomerResponse.error.message : null}
            submitting={createCustomerResponse.pending}
            fields={createCustomerForm()}
            onSubmit={({
              accountNumber, name, addressLine1, addressLine2, addressLine3, city, postcode, contactName, contactNumber, contactEmail
            }) =>
              createCustomer({
                accountNumber,
                name,
                address: {
                  addressLine1,
                  addressLine2,
                  addressLine3,
                  city,
                  postcode
                },
                contactName,
                contactNumber,
                contactEmail
              })}
          />
        </FormWrapper>
      </MainContentWrapper>

      <InfoBarWrapper>
        <CustomerInfoSidebar
          isLoading={false}
          data={null}
          terminology={terminology}
          primaryAction={(
            <ButtonLink
              $buttonType="secondary"
              to="/admin/customers/"
            >
              Cancel
            </ButtonLink>
          )}
          secondaryAction={(
            <Button
              loading={createCustomerResponse.pending}
              onClick={submitForm}
            >
              Next
            </Button>
          )}
        />
      </InfoBarWrapper>
    </Layout>
  );
};

const FormWrapper = styled(Grid.Box)`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  margin-top: ${({ theme }) => theme.spacing.margins.xl};
  form {
    width: 400px;
  }
`;

export default CreateCustomerPage;
